import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { FullPropertyData } from '../../../core/models/property.model';
import { PropertyCardData } from '../units-card/units-card';
import { CommonModule } from '@angular/common';
import { ApiProperty } from '../../../core/models/api-property.model';
import { Subscription } from 'rxjs';
import { PropertyService } from '../../../features/all-properties/services/property.service';
import { UnitService } from '../../../features/rent/services/unit.service';
import { UnitGalleryComponent } from '../unit-gallery';
import { UnitInfoComponent } from './components/unit-info';
import { BookingSidebarComponent } from './components/booking-sidebar';
import { AiAnalysisComponent } from './components/ai-analysis';
import { LocationMapComponent } from './components/location-map';
import { DayDetailsModalComponent } from './components/day-details-modal';
import { ImageModalComponent } from './components/image-modal';
import { PricingTableComponent } from './components/pricing-table';
import { AvailabilityCalendarComponent } from './components/availability-calendar/availability-calendar';
import { BookTheApartment } from '../book-the-apartment/book-the-apartment';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { RoundToTenthPipe } from '../../../core/pipes/round-to-tenth.pipe';
import {
  formatRoundedDisplayValue,
  formatRoundedNumber,
  roundToTenth,
  toFiniteNumber,
} from '../../../core/utils/number-format.util';

// ─── Local Unit interface matching GET /unit/:id/rent response ────────────────
interface UnitGalleryItem {
  secure_url?: string;
  url?: string;
  path?: string;
  public_id?: string;
  format?: string;
  resource_type?: string;
  original_filename?: string;
  _id?: string;
}
interface UnitLocation {
  country: string;
  city: string;
  area: string;
  address: string;
  coordinates?: { type: string; coordinates: number[] };
}
interface UnitOwnerContact {
  phoneNumber: string;
  email: string;
}
interface UnitOwnerPerformance {
  averageRating: number;
  totalReviews: number;
  avgResponseTimeMinutes: number | null;
  responseTimeLabel: string | null;
}
interface UnitOwner {
  _id?: string;
  type: string;
  name: string;
  role: string;
  contact: UnitOwnerContact;
  performance?: UnitOwnerPerformance;
  stats?: { listedCount: number; soldCount: number };
  avatar?: string;
}
interface UnitPricingTier {
  minDays: number;
  pricePerDay: number;
  label: string;
  _id?: string;
}
interface UnitBasePricing {
  _id: string;
  targetType: string;
  targetId: string;
  purpose: string;
  rentType: string;
  price: number;
  tiers: UnitPricingTier[];
  currency: string;
  securityDeposit?: number;
  isActive: boolean;
  commission?: { type: string; value: number };
  createdBy?: string;
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
  id?: string;
}
interface UnitSeasonalPricing {
  _id: string;
  seasonName?: string;
  targetType?: string;
  targetId?: string;
  startDate: string;
  endDate: string;
  price: number;
  tiers?: UnitPricingTier[];
  currency: string;
  priority?: number;
  isActive?: boolean;
  id?: string;
}
interface UnitAvailability {
  _id: string;
  startDate: string;
  endDate: string | null;
  status: string;
  reason?: string;
  id?: string;
}
interface NearbyLandmark {
  name: string;
  type: string;
  distance: string;
}
interface RentAnalysis {
  locationEvaluation: { rating: number; pros: string[]; description: string };
  summary: string;
  priceEvaluation: { status: string; marketComparison: string; isGoodDeal: boolean };
}
interface UnitFinalPricing {
  _id: string;
  targetType: string;
  targetId: string;
  purpose: string;
  rentType: string;
  price: number;
  tiers: (UnitPricingTier & { _id: string })[];
  currency: string;
  isActive: boolean;
  createdBy: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}
interface UnitListingPrice {
  salePrice: number;
  rentPrice: number;
  rentPeriod: string;
  currency: string;
}
interface UnitAiAnalysis {
  priceEvaluation: { status: string; marketComparison: string; isGoodDeal: boolean };
  locationEvaluation: { rating: number; pros: string[]; description: string };
  nearbyLandmarks: NearbyLandmark[];
  summary: string;
  lastUpdated: string;
  smartInsights: string[];
}
interface Unit {
  _id: string;
  isFavorite?: boolean;
  slug?: string;
  title: string;
  description: string;
  unitType: string;
  location: UnitLocation;
  gallery: UnitGalleryItem[];
  videos?: { public_id?: string; secure_url: string; format?: string; resource_type?: string; original_filename?: string; _id?: string; }[];
  video?: string;
  documents: unknown[];
  facilitiesAndServices: string[];
  nearbyLandmarks: NearbyLandmark[];
  owner?: UnitOwner;
  ownerType?: string;
  ownerId?: string;
  status?: string;
  unitNumber?: string;
  purpose?: string;
  unitArea?: number;
  bedrooms?: number;
  bathrooms?: number;
  floorNumber?: number;
  furnished?: boolean;
  adults?: number;
  children?: number;
  maxGuests?: number;
  isFeatured?: boolean;
  isTrusted?: boolean;
  averageRating?: number;
  totalReviews?: number;
  viewsCount?: number;
  // pricing
  basePricing?: UnitBasePricing;
  seasonalPricing?: UnitSeasonalPricing;
  availabilities?: UnitAvailability[];
  // analysis
  rentAnalysis?: RentAnalysis;
  aiAnalysis: UnitAiAnalysis;
  // compat fields used by existing getters
  finalPricing?: UnitFinalPricing;
  listingPrice: UnitListingPrice;
  salePricing?: {
    basePrice: number;
    pricePerMeter?: number;
    currency: string;
    _id?: string;
    plans?: {
      name_ar?: string;
      name_en?: string;
      saleType?: string;
      downPaymentPercent?: number;
      years?: number;
      interestRate?: number;
      discountPercent?: number;
      _id?: string;
    }[];
  };
  saleAnalysis?: unknown;
  projectId?: string;
  propertyId?: string;
  project?: unknown;
  updatedAt?: string;
}

@Component({
  selector: 'app-unit-details',
  imports: [
    CommonModule,
    UnitGalleryComponent,
    UnitInfoComponent,
    BookingSidebarComponent,
    AiAnalysisComponent,
    LocationMapComponent,
    DayDetailsModalComponent,
    ImageModalComponent,

    AvailabilityCalendarComponent,
    BookTheApartment,
    TranslatePipe,
    RoundToTenthPipe,
  ],
  templateUrl: './unit-details.html',
  styleUrl: './unit-details.scss',
})
export class UnitDetails implements OnInit, OnDestroy {
  property: FullPropertyData | undefined;
  apiProperty: ApiProperty | undefined;
  unitData: Unit | undefined;
  showBookingPopup = false;
  isLoading = false;
  errorMessage = '';
  private subscription: Subscription | undefined;
  private routeSubscription: Subscription | undefined;
  private languageSubscription: Subscription | undefined;
  private currentPropertyId: string | null = null;
  private loadedFromApiId: string | null = null;

  private propertyService = inject(PropertyService);
  private unitService = inject(UnitService);

  // Selected day for modal
  selectedDay: any | null = null;
  showDayDetails = false;

  // Selected date range from calendar
  selectedDateRange: { startDate: Date; endDate: Date } | null = null;

  // Selected pricing
  selectedPrice: number | null = null;
  selectedPricePeriod: string = '';
  selectedDays: number | null = null;
  selectedTotalPrice: number | null = null;

  // Image gallery properties
  currentImageIndex = 0;
  showImageModal = false;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
  ) { }

  goToLogin() {
    this.router.navigate(['/auth/login']);
  }

  goToRegister() {
    this.router.navigate(['/auth/register']);
  }

  goToSignIn() {
    this.router.navigate(['/auth/sign-in']);
  }

  goToSignUp() {
    this.router.navigate(['/auth/sign-up']);
  }

  goToRegisterPop() {
    this.router.navigate(['/auth/register-pop']);
  }

  onPriceSelected(event: {
    pricePerDay: number;
    totalPrice: number;
    days: number;
    label: string;
    isSeasonal: boolean;
    seasonStartDate?: string;
    seasonEndDate?: string;
  }) {
    this.selectedPrice = event.pricePerDay;
    this.selectedPricePeriod = event.label;
    this.selectedDays = event.days;
    this.selectedTotalPrice = event.totalPrice;
    console.log('Selected price:', event);
  }

  openBookingPopup() {
    this.showBookingPopup = true;
  }

  closeBookingPopup() {
    this.showBookingPopup = false;
  }

  ngOnInit() {
    // الحصول على ID من الـ route
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.currentPropertyId = id;
        // Check the current route to determine which endpoint to use
        const currentUrl = this.router.url;
        if (currentUrl.includes('/property/')) {
          this.loadPropertyDetails(id);
        } else {
          this.loadUnitDetails(id);
        }
      }
    });
  }

  private loadUnitDetails(id: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.unitData = undefined;
    this.apiProperty = undefined;
    this.property = undefined;

    this.subscription = this.unitService.getUnitById(id).subscribe({
      next: (response: any) => {
        console.log('🏠 Unit Details Response:', response);
        const raw = response?.unit ?? response;
        this.unitData = this.normalizeUnitData(raw);
        this.loadedFromApiId = id;
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('❌ Error loading unit details:', err);
        this.errorMessage = 'unitDetails.errors.loadUnitError';
        this.isLoading = false;
      },
    });
  }

  private normalizeUnitData(rawUnit: any): Unit {
    const normalizedNearbyLandmarks = this.normalizeNearbyLandmarks(rawUnit?.nearbyLandmarks);

    // The new response has rentAnalysis instead of aiAnalysis
    const normalizedAiAnalysis =
      rawUnit?.aiAnalysis ||
      this.mapRentAnalysisToAiAnalysis(rawUnit?.rentAnalysis, normalizedNearbyLandmarks);

    const resolvedPricing = rawUnit?.finalPricing || this.resolveRentPricing(rawUnit);
    const listingPrice = this.buildListingPrice(rawUnit, resolvedPricing);

    // Normalize owner: new response has owner._id, name, role, contact, performance
    const rawOwner = rawUnit?.owner;
    const normalizedOwner: UnitOwner | undefined = rawOwner
      ? {
        _id: rawOwner._id,
        type: rawOwner.type ?? rawUnit?.ownerType ?? '',
        name: rawOwner.name ?? '',
        role: rawOwner.role ?? '',
        contact: {
          phoneNumber: rawOwner.contact?.phoneNumber ?? '',
          email: rawOwner.contact?.email ?? '',
        },
        performance: rawOwner.performance,
        avatar: this.resolveImageUrl(rawOwner.avatar || rawOwner.image || rawOwner.profilePicture || rawOwner.logo),
      }
      : undefined;

    // Collect all raw gallery items from all possible fields
    let rawGallery: any[] = [];
    if (Array.isArray(rawUnit?.gallery)) {
      rawGallery.push(...rawUnit.gallery);
    }
    if (rawUnit?.thumbnail) {
      rawGallery.push(rawUnit.thumbnail);
    }
    if (rawUnit?.image) {
      rawGallery.push(rawUnit.image);
    }
    if (rawUnit?.coverImage) {
      rawGallery.push(rawUnit.coverImage);
    }
    if (Array.isArray(rawUnit?.images)) {
      rawGallery.push(...rawUnit.images);
    }

    const normalizedGallery: UnitGalleryItem[] = rawGallery
      .map((item: any) => {
        if (!item) return null;
        if (typeof item === 'string') {
          return { secure_url: this.resolveImageUrl(item) };
        }
        return {
          ...item,
          secure_url: this.resolveImageUrl(item.secure_url || item.url || item.path || item.public_id),
        };
      })
      .filter((item): item is UnitGalleryItem => !!item && !!item.secure_url);

    const normalizedFacilities: string[] = Array.isArray(rawUnit?.facilitiesAndServices)
      ? rawUnit.facilitiesAndServices.map((f: any) => (typeof f === 'string' ? f : f?.name || '')).filter(Boolean)
      : [];

    return {
      ...rawUnit,
      gallery: normalizedGallery,
      videos: Array.isArray(rawUnit?.videos) ? rawUnit.videos : [],
      video: rawUnit?.video,
      documents: Array.isArray(rawUnit?.documents) ? rawUnit.documents : [],
      facilitiesAndServices: normalizedFacilities,
      nearbyLandmarks: normalizedNearbyLandmarks,
      aiAnalysis: normalizedAiAnalysis,
      rentAnalysis: rawUnit?.rentAnalysis,
      finalPricing: resolvedPricing,
      listingPrice,
      owner: normalizedOwner,
      // maxGuests compat: use adults if available
      maxGuests: rawUnit?.maxGuests ?? rawUnit?.adults ?? 1,
    } as Unit;
  }

  private resolveRentPricing(unit: any): Unit['finalPricing'] | undefined {
    const activeSeasonalPricing = this.getActiveSeasonalPricing(unit?.seasonalPricing);

    if (activeSeasonalPricing) {
      return this.buildFinalPricing(activeSeasonalPricing, unit, 'seasonal');
    }

    if (unit?.basePricing) {
      return this.buildFinalPricing(unit.basePricing, unit, 'base');
    }

    return undefined;
  }

  private getActiveSeasonalPricing(seasonalPricing: any): any | null {
    if (!seasonalPricing?.startDate || !seasonalPricing?.endDate) {
      return null;
    }

    const now = new Date();
    const startDate = new Date(seasonalPricing.startDate);
    const endDate = new Date(seasonalPricing.endDate);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return null;
    }

    return now >= startDate && now <= endDate ? seasonalPricing : null;
  }

  private buildFinalPricing(
    pricing: any,
    unit: any,
    source: 'seasonal' | 'base',
  ): Unit['finalPricing'] {
    const normalizedTiers = Array.isArray(pricing?.tiers)
      ? pricing.tiers.map((tier: any) => ({
        minDays: Number(tier?.minDays ?? 1),
        pricePerDay: toFiniteNumber(tier?.pricePerDay ?? pricing?.price),
        label: tier?.label || `${tier?.minDays ?? 1} days or more`,
        _id: tier?._id || tier?.id || '',
      }))
      : [];

    return {
      _id: pricing?._id || `${source}-${unit?._id || this.currentPropertyId || 'unit'}`,
      targetType: 'unit',
      targetId: unit?._id || this.currentPropertyId || '',
      purpose: pricing?.purpose || 'rent',
      rentType: pricing?.rentType || unit?.basePricing?.rentType || 'daily',
      price: toFiniteNumber(pricing?.price),
      tiers: normalizedTiers,
      currency: pricing?.currency || 'EGP',
      isActive: true,
      createdBy: '',
      isDeleted: false,
      createdAt: '',
      updatedAt: '',
      __v: 0,
    } as Unit['finalPricing'];
  }

  private buildListingPrice(
    unit: any,
    pricing: Unit['finalPricing'] | undefined,
  ): Unit['listingPrice'] {
    return {
      salePrice: toFiniteNumber(unit?.listingPrice?.salePrice),
      rentPrice: toFiniteNumber(
        unit?.listingPrice?.rentPrice ?? pricing?.price ?? unit?.basePricing?.price ?? 0,
      ),
      rentPeriod:
        unit?.listingPrice?.rentPeriod ||
        pricing?.rentType ||
        unit?.basePricing?.rentType ||
        'daily',
      currency:
        unit?.listingPrice?.currency || pricing?.currency || unit?.basePricing?.currency || 'EGP',
    };
  }

  private mapRentAnalysisToAiAnalysis(
    rentAnalysis: any,
    nearbyLandmarks: any[] = [],
  ): Unit['aiAnalysis'] {
    return {
      priceEvaluation: {
        status: rentAnalysis?.priceEvaluation?.status || '',
        marketComparison:
          rentAnalysis?.priceEvaluation?.marketComparison ||
          rentAnalysis?.priceEvaluation?.status ||
          '',
        isGoodDeal: !!rentAnalysis?.priceEvaluation?.isGoodDeal,
      },
      locationEvaluation: {
        rating: Number(rentAnalysis?.locationEvaluation?.rating ?? 0),
        pros: Array.isArray(rentAnalysis?.locationEvaluation?.pros)
          ? rentAnalysis.locationEvaluation.pros
          : [],
        description: rentAnalysis?.locationEvaluation?.description || '',
      },
      nearbyLandmarks,
      summary: rentAnalysis?.summary || '',
      lastUpdated: rentAnalysis?.lastUpdated || '',
      smartInsights: Array.isArray(rentAnalysis?.smartInsights) ? rentAnalysis.smartInsights : [],
    } as Unit['aiAnalysis'];
  }

  private normalizeNearbyLandmarks(nearbyLandmarks: any): any[] {
    if (!Array.isArray(nearbyLandmarks)) {
      return [];
    }

    return nearbyLandmarks.map((landmark: any) => ({
      name: landmark?.name || '',
      type: landmark?.type || '',
      distance: landmark?.distance || '',
    }));
  }

  private loadPropertyDetails(id: string): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.propertyService.getPropertyById(id).subscribe({
      next: (response: any) => {
        console.log('🏠 Property Details Response:', response);

        // Handle property response format - API returns property directly without wrapper
        if (response._id) {
          // Response is the property object itself
          this.apiProperty = response;
          this.unitData = this.mapPropertyToUnit(response);
        } else if (response.property) {
          // Response has property wrapper
          this.apiProperty = response.property;
          this.unitData = this.mapPropertyToUnit(response.property);
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ Error loading property details:', error);
        this.errorMessage = 'unitDetails.errors.loadPropertyError';
        this.isLoading = false;
      },
    });
  }

  private mapPropertyToUnit(property: any): Unit {
    console.log('🔄 Mapping Property to Unit:', property);

    // Calculate unit area from price per meter if available
    let calculatedArea = 0;
    if (property.salePricing?.basePrice && property.salePricing?.pricePerMeter) {
      calculatedArea = roundToTenth(
        property.salePricing.basePrice / property.salePricing.pricePerMeter,
      );
    }

    // Map property structure to unit structure
    return {
      _id: property._id,
      slug: property.slug,
      title: property.name,
      description: property.description,
      unitType: property.type,
      location: property.location,
      gallery: property.gallery || [],
      documents: property.documents || [],
      facilitiesAndServices: property.facilitiesAndServices || [],
      nearbyLandmarks: this.normalizeNearbyLandmarks(property.nearbyLandmarks),
      project: property.project,
      owner: property.owner,
      status: property.status,
      // Add other fields with defaults or from property
      propertyId: property._id,
      projectId: property.projectId,
      ownerType: property.ownerType,
      ownerId: property.ownerId,
      unitNumber: '',
      purpose: 'sale_and_rent',
      unitArea: calculatedArea,
      bedrooms: 0,
      bathrooms: 0,
      floorNumber: property.floorsCount || 0,
      furnished: false,
      maxGuests: 0,
      isFeatured: property.isFeatured || false,
      listingPrice: {
        salePrice: property.salePricing?.basePrice || 0,
        rentPrice: 0,
        rentPeriod: 'daily',
        currency: property.salePricing?.currency || 'EGP',
      },
      // Map saleAnalysis to aiAnalysis structure
      aiAnalysis: {
        priceEvaluation: {
          status: property.saleAnalysis?.priceAnalysis?.status || '',
          marketComparison: property.saleAnalysis?.priceAnalysis?.status || '',
          isGoodDeal: property.saleAnalysis?.priceAnalysis?.priceDiffPercent <= 0,
        },
        locationEvaluation: {
          rating: property.saleAnalysis?.investmentAnalysis?.investmentScore || 0,
          pros: [],
          description: property.saleAnalysis?.summary || '',
        },
        nearbyLandmarks: this.normalizeNearbyLandmarks(property.nearbyLandmarks),
        summary: property.saleAnalysis?.summary || '',
        lastUpdated: property.saleAnalysis?.lastUpdated || '',
        smartInsights: property.saleAnalysis?.smartInsights || [],
      },
      // Add salePricing for property details
      salePricing: property.salePricing,
      saleAnalysis: property.saleAnalysis,
    } as Unit;
  }

  private handlePropertyData(property: PropertyCardData): void {
    if ('_id' in property) {
      // ?????? ???????? ???????????????? ???? ApiProperty
      this.apiProperty = property as ApiProperty;
      this.property = undefined;
    } else {
      // ?????? ???????? ???????????????? ???? FullPropertyData
      this.property = property as FullPropertyData;
      this.apiProperty = undefined;
    }
  }

  private isLikelySlug(id: string): boolean {
    return id.includes('-');
  }

  get displayTitle(): string {
    return this.unitData?.title || this.apiProperty?.name || this.property?.title || '';
  }

  get displayLocation(): string {
    if (this.unitData?.location) {
      return `${this.unitData.location.area}, ${this.unitData.location.city}`;
    }
    if (this.apiProperty?.location) {
      return `${this.apiProperty.location.area}, ${this.apiProperty.location.city}`;
    }
    return this.property?.location || this.property?.locationDetails?.area || '';
  }

  private resolveImageUrl(val: any): string {
    if (!val) return '';
    let url = '';
    if (typeof val === 'string') {
      url = val.trim();
    } else if (typeof val === 'object') {
      url = val.secure_url || val.url || val.path || val.public_id || '';
    }
    if (!url) return '';

    // If relative path like /uploads/... or uploads/..., prefix with backend baseUrl
    if (url.startsWith('/uploads') || url.startsWith('uploads/')) {
      const baseDomain = 'https://api.homigo-eg.com';
      url = url.startsWith('/') ? `${baseDomain}${url}` : `${baseDomain}/${url}`;
    }

    try {
      url = encodeURI(url);
    } catch {
      // ignore
    }

    return url;
  }

  get heroImage(): string {
    const images = this.galleryImages;
    return (
      images[0] ||
      'https://images.unsplash.com/photo-1560185007-5f0bb1866cab?w=1200&h=800&fit=crop'
    );
  }

  get galleryImages(): string[] {
    const images: string[] = [];
    const seen = new Set<string>();

    const addImg = (val: any) => {
      if (!val) return;
      // Skip videos if present in gallery array
      if (
        typeof val === 'object' &&
        (val.resource_type === 'video' || val.format?.toLowerCase() === 'mp4')
      ) {
        return;
      }
      const url = this.resolveImageUrl(val);
      if (url) {
        const lower = url.toLowerCase();
        if (
          lower.endsWith('.mp4') ||
          lower.endsWith('.webm') ||
          lower.endsWith('.mov') ||
          lower.endsWith('.avi')
        ) {
          return;
        }
        if (!seen.has(url)) {
          seen.add(url);
          images.push(url);
        }
      }
    };

    if (this.unitData?.gallery?.length) {
      this.unitData.gallery.forEach(addImg);
    }
    if (this.apiProperty?.gallery?.length) {
      this.apiProperty.gallery.forEach(addImg);
    }
    if (this.property?.images?.gallery?.length) {
      this.property.images.gallery.forEach(addImg);
    }
    if (this.property?.image) {
      addImg(this.property.image);
    }
    if ((this.unitData as any)?.thumbnail) {
      addImg((this.unitData as any).thumbnail);
    }
    if ((this.apiProperty as any)?.thumbnail) {
      addImg((this.apiProperty as any).thumbnail);
    }

    if (images.length > 0) {
      return images;
    }

    return [
      'https://images.unsplash.com/photo-1560185007-5f0bb1866cab?w=1200&h=800&fit=crop',
    ];
  }

  get galleryVideos(): string[] {
    const videos: string[] = [];
    const seen = new Set<string>();

    const addVid = (urlOrObj: any) => {
      let url = '';
      if (typeof urlOrObj === 'string') {
        url = urlOrObj.trim();
      } else if (urlOrObj && typeof urlOrObj === 'object') {
        url = urlOrObj.secure_url || urlOrObj.url || urlOrObj.path || '';
      }
      if (url) {
        if (url.startsWith('/uploads') || url.startsWith('uploads/')) {
          url = `https://api.homigo-eg.com${url.startsWith('/') ? url : '/' + url}`;
        }
        try {
          url = encodeURI(url);
        } catch {}
        if (!seen.has(url)) {
          seen.add(url);
          videos.push(url);
        }
      }
    };

    if (this.unitData?.video) {
      addVid(this.unitData.video);
    }
    if (this.unitData?.videos?.length) {
      this.unitData.videos.forEach(addVid);
    }
    if (this.unitData?.gallery?.length) {
      this.unitData.gallery.forEach((item: any) => {
        if (item && typeof item === 'object') {
          if (item.resource_type === 'video' || item.format?.toLowerCase() === 'mp4') {
            addVid(item.secure_url || item.url || item.path);
          }
        }
      });
    }
    return videos;
  }

  get isManaged(): boolean {
    return (
      (this.unitData as any)?.managedByHomiGo ??
      (this.unitData as any)?.isManaged ??
      this.property?.isManaged ??
      this.property?.badges?.managed ??
      false
    );
  }

  get isVerified(): boolean {
    return (
      this.unitData?.isTrusted ??
      (this.unitData as any)?.isVerified ??
      this.apiProperty?.isTrusted ??
      this.property?.isVerified ??
      this.property?.badges?.verified ??
      false
    );
  }

  get bedrooms(): number | null {
    return (
      this.unitData?.bedrooms ?? this.property?.bedrooms ?? this.property?.stats?.bedrooms ?? null
    );
  }

  get bathrooms(): number | null {
    return (
      this.unitData?.bathrooms ??
      this.property?.bathrooms ??
      this.property?.stats?.bathrooms ??
      null
    );
  }

  get area(): string | null {
    if (this.unitData?.unitArea !== undefined && this.unitData?.unitArea !== null) {
      return `${formatRoundedNumber(this.unitData.unitArea)} sqm`;
    }
    if (this.property?.area) return this.property.area;
    if (this.property?.stats?.area_sqm) return `${formatRoundedNumber(this.property.stats.area_sqm)} sqm`;
    return null;
  }

  get rating(): number | null {
    return (
      this.unitData?.averageRating ?? this.property?.rating ?? this.property?.stats?.rating ?? null
    );
  }

  get reviews(): number | null {
    return (
      this.unitData?.totalReviews ??
      this.property?.reviews ??
      this.property?.stats?.review_count ??
      null
    );
  }

  get floor(): string | number | null {
    return this.unitData?.floorNumber ?? this.property?.details?.floor ?? null;
  }

  get furnished(): boolean | null {
    return this.unitData?.furnished ?? this.property?.details?.furnished ?? null;
  }

  get yearBuilt(): number | null {
    return this.property?.details?.year_built ?? null;
  }

  get descriptionText(): string {
    return (
      this.unitData?.description ||
      this.apiProperty?.description ||
      this.property?.description ||
      ''
    );
  }

  get amenities(): string[] {
    const raw =
      this.unitData?.facilitiesAndServices ||
      this.property?.amenities ||
      this.apiProperty?.facilitiesAndServices ||
      [];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item: any) => (typeof item === 'string' ? item : item?.name || ''))
      .filter(Boolean);
  }

  get pricingTable() {
    // استخدام finalPricing من Unit API
    const pricing = this.effectivePricing;

    if (pricing?.tiers?.length) {
      return pricing.tiers.map((tier: { minDays: number; pricePerDay: number; label: string }) => ({
        duration_label: tier.label,
        days: tier.minDays,
        price_per_day: roundToTenth(tier.pricePerDay),
        total_price: roundToTenth(tier.pricePerDay * tier.minDays),
        discount_percent: this.calculateDiscount(tier.pricePerDay),
        savings: this.calculateSavings(tier.pricePerDay, tier.minDays),
      }));
    }
    return this.property?.pricing?.pricing_table || [];
  }

  get effectivePricing(): Unit['finalPricing'] | undefined {
    if (this.unitData?.finalPricing) {
      return this.unitData.finalPricing;
    }

    if (this.unitData?.basePricing) {
      return this.buildFinalPricing(this.unitData.basePricing, this.unitData, 'base');
    }

    return undefined;
  }

  private calculateDiscount(pricePerDay: number): number {
    if (!this.effectivePricing?.price) return 0;
    const basePrice = this.effectivePricing.price;
    return roundToTenth(((basePrice - pricePerDay) / basePrice) * 100);
  }

  private calculateSavings(pricePerDay: number, days: number): number {
    if (!this.effectivePricing?.price) return 0;
    const basePrice = this.effectivePricing.price;
    return roundToTenth((basePrice - pricePerDay) * days);
  }

  private formatDisplayNumber(value: number | string): string {
    return formatRoundedDisplayValue(value, 'en-US');
  }

  get pricingCurrency(): string {
    const currencyCandidates = [
      this.effectivePricing?.currency,
      this.unitData?.basePricing?.currency,
      this.unitData?.seasonalPricing?.currency,
      this.unitData?.listingPrice?.currency,
      this.unitData?.salePricing?.currency,
      this.property?.pricing?.currency,
    ];

    const validCurrency = currencyCandidates.find((currency) => {
      const normalized = String(currency || '')
        .trim()
        .toUpperCase();
      return /^[A-Z]{3}$/.test(normalized);
    });

    return validCurrency ? String(validCurrency).trim().toUpperCase() : 'EGP';
  }

  get priceDisplay(): string {
    // إذا تم اختيار سعر، استخدمه
    if (this.selectedPrice !== null) {
      return `${this.formatDisplayNumber(this.selectedPrice)} ${this.pricingCurrency}`;
    }

    // Check for sale pricing first (for properties)
    if (this.unitData?.salePricing?.basePrice) {
      return `${this.formatDisplayNumber(this.unitData.salePricing.basePrice)} ${this.pricingCurrency}`;
    }

    if (this.effectivePricing?.price) {
      return `${this.formatDisplayNumber(this.effectivePricing.price)} ${this.pricingCurrency}`;
    }
    if (this.unitData?.basePricing?.price) {
      return `${this.formatDisplayNumber(this.unitData.basePricing.price)} ${this.pricingCurrency}`;
    }
    if (this.unitData?.listingPrice?.rentPrice) {
      return `${this.formatDisplayNumber(this.unitData.listingPrice.rentPrice)} ${this.pricingCurrency}`;
    }
    if (this.unitData?.listingPrice?.salePrice) {
      return `${this.formatDisplayNumber(this.unitData.listingPrice.salePrice)} ${this.pricingCurrency}`;
    }
    if (this.property?.pricing?.sidebar_display_price) {
      const price = this.property.pricing.sidebar_display_price;
      return `${this.formatDisplayNumber(price.amount)} ${price.currency}`;
    }
    if (this.property?.price != null) {
      return `${this.formatDisplayNumber(this.property.price)} ${this.pricingCurrency}`;
    }
    return '';
  }

  get pricePeriodLabel(): string {
    // Return selected period label if a date range or pricing tier is selected
    if (this.selectedPricePeriod) {
      return this.selectedPricePeriod;
    }

    // For sale properties, no rent period label
    if (this.unitData?.salePricing) {
      return '';
    }

    if (this.effectivePricing?.rentType) {
      return this.getRentPeriodLabel(this.effectivePricing.rentType);
    }
    if (this.unitData?.basePricing?.rentType) {
      return this.getRentPeriodLabel(this.unitData.basePricing.rentType);
    }
    if (this.unitData?.listingPrice?.rentPeriod) {
      return this.getRentPeriodLabel(this.unitData.listingPrice.rentPeriod);
    }
    if (this.property?.pricing?.sidebar_display_price?.period_label) {
      return this.property.pricing.sidebar_display_price.period_label;
    }
    if (this.property?.priceType) {
      return this.property.priceType === 'monthly'
        ? 'Monthly'
        : this.property.priceType === 'daily'
          ? 'Daily'
          : 'For Sale';
    }
    return '';
  }

  private getRentPeriodLabel(period: string): string {
    const normalizedPeriod = period.toLowerCase();

    if (normalizedPeriod === 'daily' || normalizedPeriod === 'day') {
      return 'Daily';
    }
    if (normalizedPeriod === 'weekly' || normalizedPeriod === 'week') {
      return 'Weekly';
    }
    if (normalizedPeriod === 'monthly' || normalizedPeriod === 'month') {
      return 'Monthly';
    }
    if (normalizedPeriod === 'yearly' || normalizedPeriod === 'year') {
      return 'Yearly';
    }

    return '';
  }

  get pricePerMeter(): string | null {
    if (this.unitData?.salePricing?.pricePerMeter) {
      return `${this.formatDisplayNumber(this.unitData.salePricing.pricePerMeter)} ${this.pricingCurrency}/sqm`;
    }
    return null;
  }

  get cancellationPolicyLabel(): string {
    if (this.freeCancellationHours) {
      return `Free cancellation within ${this.formatDisplayNumber(this.freeCancellationHours)} hours`;
    }

    return 'Free cancellation within 24 hours';
  }

  get freeCancellationHours(): number | null {
    return this.property?.pricing?.free_cancellation_hours ?? null;
  }

  get availabilitySummary() {
    return this.property?.availability?.summary;
  }

  get availabilityNextDate(): string | null {
    return this.property?.availability?.next_available_date ?? null;
  }

  get availabilityDaysUntil(): number | null {
    return this.property?.availability?.days_until_next_available ?? null;
  }

  get availabilityCurrentPeriod() {
    return this.property?.availability?.current_period;
  }

  get unitAvailabilityPeriods() {
    return Array.isArray(this.unitData?.availabilities) ? this.unitData.availabilities : null;
  }

  get bookingRentalType(): 'daily' | 'weekly' | 'monthly' | 'yearly' {
    const rentType =
      this.effectivePricing?.rentType ||
      this.unitData?.basePricing?.rentType ||
      this.unitData?.listingPrice?.rentPeriod ||
      'daily';
    const normalized = rentType.toLowerCase();

    if (normalized === 'day' || normalized === 'daily') return 'daily';
    if (normalized === 'week' || normalized === 'weekly') return 'weekly';
    if (normalized === 'month' || normalized === 'monthly') return 'monthly';
    if (normalized === 'year' || normalized === 'yearly') return 'yearly';
    return 'daily';
  }

  get smartAnalysis() {
    return this.unitData?.aiAnalysis || this.property?.smart_analysis;
  }

  get locationLat(): number | null {
    return (
      this.unitData?.location?.coordinates?.coordinates?.[1] ??
      this.property?.location_details?.latitude ??
      this.property?.locationDetails?.latitude ??
      this.apiProperty?.location?.coordinates?.coordinates?.[1] ??
      null
    );
  }

  get locationLng(): number | null {
    return (
      this.unitData?.location?.coordinates?.coordinates?.[0] ??
      this.property?.location_details?.longitude ??
      this.property?.locationDetails?.longitude ??
      this.apiProperty?.location?.coordinates?.coordinates?.[0] ??
      null
    );
  }

  get locationCoordinates(): string {
    const lat = this.locationLat;
    const lng = this.locationLng;
    if (lat !== null && lng !== null) {
      return `Lat: ${this.formatDisplayNumber(lat)}, Lng: ${this.formatDisplayNumber(lng)}`;
    }
    return 'Lat: 30.0444, Lng: 31.2357';
  }

  get locationAddress(): string {
    return this.unitData?.location?.address || this.apiProperty?.location?.address || '';
  }

  get locationArea(): string {
    return this.unitData?.location?.area || this.apiProperty?.location?.area || '';
  }

  get locationCity(): string {
    return this.unitData?.location?.city || this.apiProperty?.location?.city || '';
  }

  get locationCountry(): string {
    return this.unitData?.location?.country || this.apiProperty?.location?.country || '';
  }

  get nearbyPlaces() {
    if (this.unitData?.aiAnalysis?.nearbyLandmarks?.length) {
      return this.unitData.aiAnalysis.nearbyLandmarks.map((landmark) => ({
        name: landmark.name,
        type: landmark.type,
        distance: landmark.distance,
      }));
    }
    if (this.unitData?.nearbyLandmarks?.length) {
      return this.normalizeNearbyLandmarks(this.unitData.nearbyLandmarks);
    }
    return this.property?.location_details?.nearby_places || [];
  }

  get ownerInfo() {
    if (!this.unitData?.owner) return null;

    return {
      id: this.unitData.owner._id || this.unitData.ownerId || '',
      name: this.unitData.owner.name,
      type: this.unitData.owner.type,
      role: this.unitData.owner.role,
      phoneNumber: this.unitData.owner.contact?.phoneNumber || '',
      email: this.unitData.owner.contact?.email || '',
      avatar:
        this.unitData.owner.avatar ||
        (this.unitData.owner as any)?.image ||
        (this.unitData.owner as any)?.profilePicture ||
        '',
      performance: this.unitData.owner.performance || null,
    };
  }

  get unitType(): string {
    return this.unitData?.unitType || this.property?.type || '';
  }

  get unitNumber(): string {
    return this.unitData?.unitNumber || '';
  }

  get adults(): number {
    return this.unitData?.adults || 0;
  }

  get children(): number {
    return this.unitData?.children || 0;
  }

  get viewsCount(): number {
    return this.unitData?.viewsCount || 0;
  }

  get securityDeposit(): number | null {
    return this.unitData?.basePricing?.securityDeposit ?? null;
  }

  get commission(): any | null {
    return this.unitData?.basePricing?.commission ?? null;
  }

  get ownerName(): string {
    return this.unitData?.owner?.name || this.property?.owner?.name || '';
  }

  get ownerType(): string {
    return this.unitData?.owner?.type || '';
  }

  get ownerRole(): string {
    return this.unitData?.owner?.role || '';
  }

  get ownerPhone(): string {
    return this.unitData?.owner?.contact?.phoneNumber || '';
  }

  get ownerEmail(): string {
    return this.unitData?.owner?.contact?.email || '';
  }

  get safetyTips(): string[] {
    return this.property?.safety_tips || [];
  }

  get defaultSafetyTips(): string[] {
    return ['unitDetails.safetyTips.noPayment', 'unitDetails.safetyTips.verifyDocs', 'unitDetails.safetyTips.securePayment'];
  }

  // AI Analysis getters
  get hasPriceEvaluation(): boolean {
    return !!this.unitData?.aiAnalysis?.priceEvaluation;
  }

  get hasLocationEvaluation(): boolean {
    return !!this.unitData?.aiAnalysis?.locationEvaluation;
  }

  get hasAiSummary(): boolean {
    return !!this.unitData?.aiAnalysis?.summary;
  }

  // Calendar event handler
  onDateClick(day: any): void {
    this.selectedDay = day;
    this.showDayDetails = true;
    console.log('Date clicked:', day.fullDate, 'Status:', day.status);
  }

  onDateRangeSelected(range: { startDate: Date; endDate: Date; totalPrice?: number; totalDays?: number }): void {
    console.log('📅 Date range selected:', range);
    this.selectedDateRange = range;

    // Use calendar-calculated exact pricing if available
    if (range.totalPrice !== undefined && range.totalDays !== undefined) {
      console.log(`📊 Number of days: ${range.totalDays}`);
      this.selectedDays = range.totalDays;
      this.selectedPrice = range.totalPrice;
      this.selectedTotalPrice = range.totalPrice;
      this.selectedPricePeriod = `Total for ${range.totalDays} nights`;
      return;
    }

    const days = Math.ceil(
      (range.endDate.getTime() - range.startDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    console.log('📊 Number of days:', days);
    this.selectedDays = days;

    // Check if selected dates fall in active seasonal pricing window
    const sp = this.unitData?.seasonalPricing;
    if (sp?.startDate && sp?.endDate && sp?.price) {
      const spStart = new Date(sp.startDate);
      const spEnd = new Date(sp.endDate);
      const rangeStart = range.startDate;
      const rangeEnd = range.endDate;

      const overlaps = rangeStart <= spEnd && rangeEnd >= spStart;

      if (overlaps) {
        // Find best matching tier from seasonal pricing
        const matchingTier = sp.tiers
          ?.filter((t) => days >= t.minDays)
          .sort((a, b) => b.minDays - a.minDays)[0];

        const pricePerDay = toFiniteNumber(matchingTier?.pricePerDay ?? sp.price);
        const total = roundToTenth(pricePerDay * days);

        this.selectedPrice = total; // Set display price to total
        this.selectedTotalPrice = total;
        this.selectedPricePeriod = `Total for ${days} nights`;

        console.log('🌟 Seasonal pricing applied:', pricePerDay, '×', days, '=', total);
        return;
      }
    }

    // Fallback: use base pricing tiers
    const base = this.unitData?.basePricing;
    if (base?.price) {
      const matchingTier = base.tiers
        ?.filter((t) => days >= t.minDays)
        .sort((a, b) => b.minDays - a.minDays)[0];

      const pricePerDay = toFiniteNumber(matchingTier?.pricePerDay ?? base.price);
      const total = roundToTenth(pricePerDay * days);

      this.selectedPrice = total; // Set display price to total
      this.selectedTotalPrice = total;
      this.selectedPricePeriod = `Total for ${days} nights`;

      console.log('💰 Base pricing applied:', pricePerDay, '×', days, '=', total);
    }
  }

  closeDayDetails(): void {
    this.showDayDetails = false;
    this.selectedDay = null;
  }

  getDayStatusText(day: any): string {
    switch (day.status) {
      case 'available':
        return 'Available for booking';
      case 'occupied':
        return 'Already booked';
      case 'pending':
        return 'Pending confirmation';
      case 'past':
        return 'Past date';
      default:
        return 'Unknown status';
    }
  }

  // Image gallery methods
  selectImage(index: number): void {
    if (index >= 0 && index < this.galleryImages.length) {
      this.currentImageIndex = index;
    }
  }

  openImageModal(): void {
    this.showImageModal = true;
    // Focus the modal for keyboard navigation
    setTimeout(() => {
      const modal = document.querySelector('[tabindex="0"]') as HTMLElement;
      if (modal) {
        modal.focus();
      }
    }, 100);
  }

  closeImageModal(): void {
    this.showImageModal = false;
  }

  nextImage(): void {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.galleryImages.length;
  }

  previousImage(): void {
    this.currentImageIndex =
      this.currentImageIndex === 0 ? this.galleryImages.length - 1 : this.currentImageIndex - 1;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }
}
